import React, { useState, useEffect } from 'react';
import './optimizedImage.scss';

const OptimizedImage = ({ 
  src, 
  alt, 
  className = '', 
  placeholder = null,
  sizes = '100vw',
  priority = false 
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isInView, setIsInView] = useState(priority);

  // Generate WebP version path
  const getWebPSrc = (originalSrc) => {
    const lastDotIndex = originalSrc.lastIndexOf('.');
    if (lastDotIndex === -1) return originalSrc;
    return originalSrc.substring(0, lastDotIndex) + '.webp';
  };

  // Generate optimized version path
  const getOptimizedSrc = (originalSrc) => {
    // If it's already an optimized path, return as is
    if (originalSrc.includes('/optimized/')) {
      return originalSrc;
    }
    
    // Convert to optimized path
    const parts = originalSrc.split('/');
    const directory = parts[parts.length - 2];
    
    if (directory === 'patiopurpura') {
      return originalSrc.replace('/patiopurpura/', '/optimized/patiopurpura/');
    } else {
      return originalSrc.replace('/assets/', '/assets/optimized/');
    }
  };

  const optimizedSrc = getOptimizedSrc(src);
  const webpSrc = getWebPSrc(optimizedSrc);

  useEffect(() => {
    if (priority) return; // Skip intersection observer for priority images

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px 0px', // Start loading 50px before the image comes into view
        threshold: 0.01
      }
    );

    const imgElement = document.querySelector(`[data-src="${src}"]`);
    if (imgElement) {
      observer.observe(imgElement);
    }

    return () => observer.disconnect();
  }, [src, priority]);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setIsError(true);
    // Fallback to original image if optimized version fails
    if (src !== optimizedSrc) {
      setIsInView(true);
    }
  };

  if (isError && src === optimizedSrc) {
    // If optimized version failed, try original
    return (
      <img
        src={src}
        alt={alt}
        className={`optimized-image ${className}`}
        onLoad={handleLoad}
        onError={() => setIsError(true)}
      />
    );
  }

  return (
    <div 
      className={`optimized-image-container ${className}`}
      data-src={src}
    >
      {/* Placeholder */}
      {placeholder && !isLoaded && (
        <div className="image-placeholder">
          {placeholder}
        </div>
      )}
      
      {/* Loading skeleton */}
      {!placeholder && !isLoaded && (
        <div className="image-skeleton" />
      )}
      
      {/* Optimized image */}
      {isInView && (
        <picture>
          <source srcSet={webpSrc} type="image/webp" />
          <img
            src={optimizedSrc}
            alt={alt}
            className={`optimized-image ${isLoaded ? 'loaded' : ''}`}
            sizes={sizes}
            loading={priority ? 'eager' : 'lazy'}
            onLoad={handleLoad}
            onError={handleError}
          />
        </picture>
      )}
    </div>
  );
};

export default OptimizedImage;
